import {
    Autocomplete, Box, Card, CardContent, Chip, CircularProgress,
    IconButton, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tabs, TextField,
    Tooltip, Typography
} from "@mui/material"
import {
    AccountBalanceOutlined, CompareArrowsOutlined,
    RefreshOutlined, SearchOutlined, SendOutlined, SwapHorizOutlined,
    TrendingDown, TrendingUp
} from "@mui/icons-material"
import { FC, Fragment, useEffect, useState } from "react"
import PageTitle from "../Common/PageTitle"
import { LoadingButton } from "@mui/lab"
import ConfirmPinDialog from "../Common/ConfirmPinDialog"
import { Apis, numberWithCommas } from "../../helpers"
import { EventsEmit } from "../../configs/events"
import moment from "moment"
import { LinkTab } from "../Tenants/Tenant"
import { theme } from "../../theme"
import { TABLE_HEIGHT } from "../../configs"

// ── Types ─────────────────────────────────────────────────────────────────────

interface TransactionItem {
    transactionId: string
    date: string
    reference: string
    description: string
    amount: number
    type: 'credit' | 'debit'
    status: 'completed' | 'pending' | 'failed'
    accountNumber?: string
}

type ActionResult = {
    status: 'success' | 'pending' | 'failed'
    message: string
    transactionId?: string
    referenceId?: string
    bankFee?: number
} | null

interface BankOption {
    bank_name: string
    bank_code: string
}

interface StateProps {
    tab: number
    // Overview
    loading: boolean
    refreshing: boolean
    totalBalance: number
    // Statements
    startDate: string
    endDate: string
    transactions: TransactionItem[]
    transactionsLoading: boolean
    // Send to Mpesa
    mpesaPhone: string
    mpesaAmount: string
    mpesaNarration: string
    mpesaResult: ActionResult
    // Internal Transfer
    destinationAccount: string
    transferAmount: string
    transferNarration: string
    transferResult: ActionResult
    // Pesalink Transfer
    banks: BankOption[]
    banksLoading: boolean
    pesalinkBankCode: string
    pesalinkAccount: string
    pesalinkAmount: string
    pesalinkNarration: string
    pesalinkResult: ActionResult
    // PIN confirmation
    pinTarget: 'mpesa' | 'transfer' | 'pesalink' | null
    pinProcessing: boolean
    pinError: string | null
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const PRIMARY = theme.lightTheme.palette.primary.main

const txStatusColor = (s: string): 'success' | 'warning' | 'error' | 'default' =>
    s === 'completed' ? 'success' : s === 'pending' ? 'warning' : s === 'failed' ? 'error' : 'default'

const resultStatusColor = (s: string): 'success' | 'warning' | 'error' | 'default' =>
    s === 'success' ? 'success' : s === 'pending' ? 'warning' : s === 'failed' ? 'error' : 'default'

const defaultStartDate = () => moment().subtract(30, 'days').format('YYYY-MM-DD')
const defaultEndDate = () => moment().format('YYYY-MM-DD')

// ── Result card ────────────────────────────────────────────────────────────────

const ResultCard: FC<{ result: ActionResult }> = ({ result }) => {
    if (!result) return <Fragment />
    const borderColor = result.status === 'success' ? 'green'
        : result.status === 'pending' ? 'orange' : '#d32f2f'
    return (
        <Card variant="outlined" sx={{ mt: 3, borderColor, borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        label={result.status}
                        color={resultStatusColor(result.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                    />
                    <Typography sx={{ fontSize: 14 }}>{result.message}</Typography>
                </Box>
                {(result.transactionId || result.referenceId) && (
                    <Typography sx={{ fontSize: 12, color: '#888', mt: 0.5 }}>
                        Ref: {result.referenceId || result.transactionId}
                    </Typography>
                )}
                {typeof result.bankFee === 'number' && (
                    <Typography sx={{ fontSize: 12, color: '#888', mt: 0.5 }}>
                        Bank fee: Ksh {numberWithCommas(result.bankFee)}
                    </Typography>
                )}
            </CardContent>
        </Card>
    )
}

// ── Main component ────────────────────────────────────────────────────────────

const BankDashboard: FC = () => {
    const [state, setState] = useState<StateProps>({
        tab: 0,
        loading: true,
        refreshing: false,
        totalBalance: 0,
        startDate: defaultStartDate(),
        endDate: defaultEndDate(),
        transactions: [],
        transactionsLoading: false,
        mpesaPhone: '',
        mpesaAmount: '',
        mpesaNarration: '',
        mpesaResult: null,
        destinationAccount: '',
        transferAmount: '',
        transferNarration: '',
        transferResult: null,
        banks: [],
        banksLoading: false,
        pesalinkBankCode: '',
        pesalinkAccount: '',
        pesalinkAmount: '',
        pesalinkNarration: '',
        pesalinkResult: null,
        pinTarget: null,
        pinProcessing: false,
        pinError: null
    })

    const {
        tab, loading, refreshing, totalBalance,
        startDate, endDate, transactions, transactionsLoading,
        mpesaPhone, mpesaAmount, mpesaNarration, mpesaResult,
        destinationAccount, transferAmount, transferNarration, transferResult,
        banks, banksLoading, pesalinkBankCode, pesalinkAccount, pesalinkAmount, pesalinkNarration, pesalinkResult,
        pinTarget, pinProcessing, pinError
    } = state

    // ── Fetchers ──────────────────────────────────────────────────────────────

    const fetchBalance = ({ refreshing }: { refreshing?: boolean } = {}) => {
        if (refreshing) setState(s => ({ ...s, refreshing: true }))
        Apis({ module: 'Banks', action: 'getCoopBalance' })
            .then((resp: any) => {
                if (resp?.error)
                    EventsEmit('app', { info: { message: resp.message || 'Failed to fetch balance.', status: 'error' } })
                setState(s => ({
                    ...s,
                    totalBalance: resp?.balance || 0,
                    loading: false,
                    refreshing: false
                }))
            })
            .catch(() => setState(s => ({ ...s, loading: false, refreshing: false })))
    }

    const fetchStatements = ({ refreshing }: { refreshing?: boolean } = {}) => {
        setState(s => ({ ...s, transactionsLoading: true, refreshing: Boolean(refreshing) }))
        Apis({
            module: 'Banks',
            action: 'getBankStatements',
            startDate,
            endDate
        })
            .then((resp: any) => {
                if (resp?.error)
                    EventsEmit('app', { info: { message: resp.message || 'Failed to fetch statements.', status: 'error' } })
                setState(s => ({
                    ...s,
                    transactions: resp?.transactions || [],
                    transactionsLoading: false,
                    refreshing: false
                }))
            })
            .catch(() => setState(s => ({ ...s, transactionsLoading: false, refreshing: false })))
    }

    const refreshAll = () => {
        setState(s => ({ ...s, refreshing: true, transactionsLoading: true }))
        Promise.all([
            Apis({ module: 'Banks', action: 'getCoopBalance' }),
            Apis({ module: 'Banks', action: 'getBankStatements', startDate, endDate })
        ]).then(([balanceResp, statementsResp]: any[]) => {
            if (balanceResp?.error)
                EventsEmit('app', { info: { message: balanceResp.message || 'Failed to fetch balance.', status: 'error' } })
            if (statementsResp?.error)
                EventsEmit('app', { info: { message: statementsResp.message || 'Failed to fetch statements.', status: 'error' } })
            setState(s => ({
                ...s,
                totalBalance: balanceResp?.balance || 0,
                transactions: statementsResp?.transactions || [],
                loading: false,
                refreshing: false,
                transactionsLoading: false
            }))
        }).catch(() => setState(s => ({ ...s, loading: false, refreshing: false, transactionsLoading: false })))
    }

    const sendMpesa = (pin: string) => {
        if (!mpesaPhone || !mpesaAmount) return
        setState(s => ({ ...s, pinProcessing: true, pinError: null }))
        Apis({
            module: 'Banks',
            action: 'mpesaSend',
            mobileNumber: mpesaPhone,
            amount: Number(mpesaAmount),
            narration: mpesaNarration || 'Payment',
            pin
        })
            .then((resp: any) => {
                if (resp?.status === 'error') {
                    setState(s => ({ ...s, pinProcessing: false, pinError: resp.message || 'Wrong PIN.' }))
                    return
                }
                setState(s => ({
                    ...s,
                    pinProcessing: false,
                    pinTarget: null,
                    pinError: null,
                    mpesaResult: {
                        status: resp?.status || 'pending',
                        message: resp?.message || 'Request submitted.',
                        referenceId: resp?.referenceId,
                        transactionId: resp?.transactionId
                    }
                }))
                if (resp?.message)
                    EventsEmit('app', { info: { message: resp.message, status: 'success' } })
            })
            .catch(() => setState(s => ({
                ...s,
                pinProcessing: false,
                pinTarget: null,
                mpesaResult: { status: 'failed', message: 'Transaction failed. Please try again.' }
            })))
    }

    const sendTransfer = (pin: string) => {
        if (!destinationAccount || !transferAmount) return
        setState(s => ({ ...s, pinProcessing: true, pinError: null }))
        Apis({
            module: 'Banks',
            action: 'internalTransfer',
            destinationAccount,
            amount: Number(transferAmount),
            narration: transferNarration || 'Internal Transfer',
            pin
        })
            .then((resp: any) => {
                if (resp?.status === 'error') {
                    setState(s => ({ ...s, pinProcessing: false, pinError: resp.message || 'Wrong PIN.' }))
                    return
                }
                setState(s => ({
                    ...s,
                    pinProcessing: false,
                    pinTarget: null,
                    pinError: null,
                    transferResult: {
                        status: resp?.status || 'pending',
                        message: resp?.message || 'Transfer submitted.',
                        referenceId: resp?.referenceId,
                        transactionId: resp?.transactionId
                    }
                }))
                if (resp?.message)
                    EventsEmit('app', { info: { message: resp.message, status: 'success' } })
            })
            .catch(() => setState(s => ({
                ...s,
                pinProcessing: false,
                pinTarget: null,
                transferResult: { status: 'failed', message: 'Transfer failed. Please try again.' }
            })))
    }

    const fetchBanks = () => {
        setState(s => ({ ...s, banksLoading: true }))
        Apis({ module: 'Banks', action: 'getBanks' })
            .then((resp: any) => setState(s => ({ ...s, banks: resp || [], banksLoading: false })))
            .catch(() => setState(s => ({ ...s, banksLoading: false })))
    }

    const sendPesalink = (pin: string) => {
        if (!pesalinkBankCode || !pesalinkAccount || !pesalinkAmount) return
        setState(s => ({ ...s, pinProcessing: true, pinError: null }))
        Apis({
            module: 'Banks',
            action: 'pesalinkTransfer',
            bankCode: pesalinkBankCode,
            destinationAccount: pesalinkAccount,
            amount: Number(pesalinkAmount),
            narration: pesalinkNarration || 'Pesalink Transfer',
            pin
        })
            .then((resp: any) => {
                if (resp?.status === 'error') {
                    setState(s => ({ ...s, pinProcessing: false, pinError: resp.message || 'Wrong PIN.' }))
                    return
                }
                setState(s => ({
                    ...s,
                    pinProcessing: false,
                    pinTarget: null,
                    pinError: null,
                    pesalinkResult: {
                        status: resp?.status || 'pending',
                        message: resp?.message || 'Transfer submitted.',
                        referenceId: resp?.referenceId,
                        transactionId: resp?.transactionId,
                        bankFee: resp?.bankFee
                    }
                }))
                if (resp?.message)
                    EventsEmit('app', { info: { message: resp.message, status: 'success' } })
            })
            .catch(() => setState(s => ({
                ...s,
                pinProcessing: false,
                pinTarget: null,
                pesalinkResult: { status: 'failed', message: 'Transfer failed. Please try again.' }
            })))
    }

    useEffect(() => { fetchBalance(); fetchBanks(); fetchStatements() }, [])

    // ── Tab: Overview ─────────────────────────────────────────────────────────

    const OverviewTab = (
        <Box>
            <Card sx={{
                background: `linear-gradient(135deg, ${PRIMARY} 0%, #332a05 100%)`,
                color: '#fff',
                borderRadius: 3,
                mb: 4,
                maxWidth: 440
            }}>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <AccountBalanceOutlined sx={{ mr: 1, opacity: 0.75, fontSize: 20 }} />
                        <Typography sx={{ opacity: 0.8, fontSize: 14 }}>Account Balance</Typography>
                        <Tooltip title="Refresh">
                            <IconButton
                                size="small"
                                sx={{ ml: 'auto', color: 'rgba(255,255,255,0.7)' }}
                                onClick={refreshAll}
                                disabled={refreshing}
                            >
                                {refreshing
                                    ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                                    : <RefreshOutlined fontSize="small" />
                                }
                            </IconButton>
                        </Tooltip>
                    </Box>
                    {loading
                        ? <CircularProgress size={28} sx={{ color: '#fff' }} />
                        : <Typography variant="h3" sx={{ fontWeight: 'bold', letterSpacing: 0.5 }}>
                            Ksh {numberWithCommas(totalBalance)}
                        </Typography>
                    }
                </CardContent>
            </Card>

            {/* Date range controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField
                    label="From"
                    type="date"
                    size="small"
                    value={startDate}
                    onChange={e => setState({ ...state, startDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 180 }}
                />
                <TextField
                    label="To"
                    type="date"
                    size="small"
                    value={endDate}
                    onChange={e => setState({ ...state, endDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 180 }}
                />
                <LoadingButton
                    variant="contained"
                    size="small"
                    loading={transactionsLoading}
                    onClick={() => fetchStatements()}
                    startIcon={<SearchOutlined />}
                    sx={{ textTransform: 'none', height: 40 }}
                >
                    Fetch
                </LoadingButton>
            </Box>

            {transactionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
                    <CircularProgress />
                </Box>
            ) : !transactions.length ? (
                <Typography sx={{ color: '#bbb', textAlign: 'center', mt: 10 }}>
                    No transactions found for the selected date range.
                </Typography>
            ) : (
                <Paper>
                    <TableContainer sx={{ maxHeight: TABLE_HEIGHT }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {['Date', 'Reference', 'Description', 'Amount (Ksh)', 'Type', 'Status'].map(h => (
                                        <TableCell
                                            key={h}
                                            sx={{
                                                fontWeight: 'bold',
                                                ...(h === 'Description' && { width: 200, maxWidth: 200 })
                                            }}
                                        >{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.map((tx, i) => (
                                    <TableRow hover key={tx.transactionId || i}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {moment(tx.date).format('DD/MM/YYYY HH:mm')}
                                        </TableCell>
                                        <TableCell>{tx.reference || '—'}</TableCell>
                                        <TableCell sx={{ width: 200, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            <Tooltip title={tx.description} placement="top-start">
                                                <span>{tx.description}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: tx.type === 'credit' ? 'green' : 'inherit' }}>
                                            {tx.type === 'credit' ? '+' : '−'} {numberWithCommas(tx.amount)}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {tx.type === 'credit'
                                                    ? <TrendingUp sx={{ color: 'green', fontSize: 18 }} />
                                                    : <TrendingDown sx={{ color: '#e65100', fontSize: 18 }} />
                                                }
                                                <Typography sx={{ fontSize: 13, textTransform: 'capitalize' }}>{tx.type}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={tx.status}
                                                color={txStatusColor(tx.status)}
                                                size="small"
                                                sx={{ textTransform: 'capitalize' }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    )

    // ── Tab: Send to Mpesa ────────────────────────────────────────────────────

    const MpesaTab = (
        <Box sx={{ maxWidth: 460 }}>
            <Typography sx={{ fontWeight: 'bold', mb: 3, fontSize: 18 }}>Send Money to Mpesa</Typography>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <TextField
                        fullWidth
                        label="Mobile Number"
                        placeholder="0712345678"
                        type="tel"
                        value={mpesaPhone}
                        onChange={e => setState({ ...state, mpesaPhone: e.target.value, mpesaResult: null })}
                        sx={{ mb: 3 }}
                    />
                    <TextField
                        fullWidth
                        label="Amount (Ksh)"
                        type="number"
                        inputProps={{ min: 1 }}
                        value={mpesaAmount}
                        onChange={e => setState({ ...state, mpesaAmount: e.target.value, mpesaResult: null })}
                        sx={{ mb: 3 }}
                    />
                    <TextField
                        fullWidth
                        label="Note"
                        value={mpesaNarration}
                        onChange={e => setState({ ...state, mpesaNarration: e.target.value })}
                        sx={{ mb: 3 }}
                    />
                    <LoadingButton
                        fullWidth
                        variant="contained"
                        disabled={!mpesaPhone || !mpesaAmount}
                        onClick={() => setState({ ...state, pinTarget: 'mpesa', pinError: null })}
                        startIcon={<SendOutlined />}
                        sx={{ textTransform: 'none', borderRadius: 2, py: 1.5 }}
                    >
                        {mpesaAmount
                            ? `Send Ksh ${numberWithCommas(Number(mpesaAmount))}`
                            : 'Send'
                        }
                    </LoadingButton>
                    <ResultCard result={mpesaResult} />
                </CardContent>
            </Card>
        </Box>
    )

    // ── Tab: Internal Transfer ────────────────────────────────────────────────

    const TransferTab = (
        <Box sx={{ maxWidth: 460 }}>
            <Typography sx={{ fontWeight: 'bold', mb: 3, fontSize: 18 }}>Internal Funds Transfer</Typography>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <TextField
                        fullWidth
                        label="Destination Account Number"
                        placeholder="Recipient Co-op account number"
                        value={destinationAccount}
                        onChange={e => setState({ ...state, destinationAccount: e.target.value, transferResult: null })}
                        sx={{ mb: 3 }}
                    />
                    <TextField
                        fullWidth
                        label="Amount (Ksh)"
                        type="number"
                        inputProps={{ min: 1 }}
                        value={transferAmount}
                        onChange={e => setState({ ...state, transferAmount: e.target.value, transferResult: null })}
                        sx={{ mb: 3 }}
                    />
                    <TextField
                        fullWidth
                        label="Note"
                        value={transferNarration}
                        onChange={e => setState({ ...state, transferNarration: e.target.value })}
                        sx={{ mb: 3 }}
                    />
                    <LoadingButton
                        fullWidth
                        variant="contained"
                        disabled={!destinationAccount || !transferAmount}
                        onClick={() => setState({ ...state, pinTarget: 'transfer', pinError: null })}
                        startIcon={<CompareArrowsOutlined />}
                        sx={{ textTransform: 'none', borderRadius: 2, py: 1.5 }}
                    >
                        {transferAmount
                            ? `Transfer Ksh ${numberWithCommas(Number(transferAmount))}`
                            : 'Transfer'
                        }
                    </LoadingButton>
                    <ResultCard result={transferResult} />
                </CardContent>
            </Card>
        </Box>
    )

    // ── Tab: Pesalink Transfer ────────────────────────────────────────────────

    const PesalinkTab = (
        <Box sx={{ maxWidth: 460 }}>
            <Typography sx={{ fontWeight: 'bold', mb: 3, fontSize: 18 }}>Pesalink Transfer</Typography>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Autocomplete
                        options={banks}
                        loading={banksLoading}
                        getOptionLabel={(option) => option.bank_name}
                        isOptionEqualToValue={(option, value) => option.bank_code === value.bank_code}
                        value={banks.find(b => b.bank_code === pesalinkBankCode) || null}
                        onChange={(_, value) => setState({ ...state, pesalinkBankCode: value?.bank_code || '', pesalinkResult: null })}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Recipient Bank"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <Fragment>
                                            {banksLoading ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </Fragment>
                                    )
                                }}
                            />
                        )}
                        sx={{ mb: 3 }}
                    />
                    <TextField
                        fullWidth
                        label="Destination Account Number"
                        placeholder="Recipient bank account number"
                        value={pesalinkAccount}
                        onChange={e => setState({ ...state, pesalinkAccount: e.target.value, pesalinkResult: null })}
                        sx={{ mb: 3 }}
                    />
                    <TextField
                        fullWidth
                        label="Amount (Ksh)"
                        type="number"
                        inputProps={{ min: 1 }}
                        value={pesalinkAmount}
                        onChange={e => setState({ ...state, pesalinkAmount: e.target.value, pesalinkResult: null })}
                        sx={{ mb: 3 }}
                    />
                    <TextField
                        fullWidth
                        label="Note"
                        value={pesalinkNarration}
                        onChange={e => setState({ ...state, pesalinkNarration: e.target.value })}
                        sx={{ mb: 3 }}
                    />
                    <LoadingButton
                        fullWidth
                        variant="contained"
                        disabled={!pesalinkBankCode || !pesalinkAccount || !pesalinkAmount}
                        onClick={() => setState({ ...state, pinTarget: 'pesalink', pinError: null })}
                        startIcon={<SwapHorizOutlined />}
                        sx={{ textTransform: 'none', borderRadius: 2, py: 1.5 }}
                    >
                        {pesalinkAmount
                            ? `Transfer Ksh ${numberWithCommas(Number(pesalinkAmount))}`
                            : 'Transfer'
                        }
                    </LoadingButton>
                    <ResultCard result={pesalinkResult} />
                </CardContent>
            </Card>
        </Box>
    )

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Box sx={{ pr: 5, height: '100%' }}>
            <PageTitle title="Bank Dashboard" />
            <Tabs
                value={tab}
                onChange={(_, v) => setState({ ...state, tab: v })}
                sx={{ mt: 3, ml: 5 }}
            >
                {['Overview', 'Send to Mpesa', 'Internal Transfer', 'Pesalink Transfer'].map(label => (
                    <LinkTab key={label} label={label} />
                ))}
            </Tabs>

            <Box sx={{ mt: 4, ml: 5 }}>
                {tab === 0 && OverviewTab}
                {tab === 1 && MpesaTab}
                {tab === 2 && TransferTab}
                {tab === 3 && PesalinkTab}
            </Box>

            {pinTarget && (
                <ConfirmPinDialog
                    title={
                        pinTarget === 'mpesa' ? 'Confirm Mpesa Send'
                            : pinTarget === 'transfer' ? 'Confirm Transfer'
                                : 'Confirm Pesalink Transfer'
                    }
                    message={
                        pinTarget === 'mpesa'
                            ? `Enter your PIN to send Ksh ${numberWithCommas(Number(mpesaAmount) || 0)} to ${mpesaPhone}.`
                            : pinTarget === 'transfer'
                                ? `Enter your PIN to transfer Ksh ${numberWithCommas(Number(transferAmount) || 0)} to account ${destinationAccount}.`
                                : `Enter your PIN to transfer Ksh ${numberWithCommas(Number(pesalinkAmount) || 0)} to ${banks.find(b => b.bank_code === pesalinkBankCode)?.bank_name || 'the selected bank'}.`
                    }
                    processing={pinProcessing}
                    errorMessage={pinError}
                    onConfirm={(pin) => pinTarget === 'mpesa' ? sendMpesa(pin) : pinTarget === 'transfer' ? sendTransfer(pin) : sendPesalink(pin)}
                    close={() => setState({ ...state, pinTarget: null, pinError: null })}
                />
            )}
        </Box>
    )
}

export default BankDashboard
